import {NavLink, Outlet, useLocation, useNavigate} from "react-router-dom";
import {buildRoute} from "@router/RouterUtils";
import {AppRoute} from "@router/RoutingModels";

import {useEffect, useRef, useState} from "react";
import {useAuthStore} from "@store/AuthStore";
import {usePrivateBootStatus, useProjectId, useProjectLanguage} from "@store/AppContextStore";
import {usePrivateBootstrap} from "@lib/Bootstrap/usePrivateBootstrap";
import {getValidAccessToken} from "@msal/tokenService";
import {useShallow} from "zustand/react/shallow";
import {i18n} from "@i18n/i18n";
import {useProjectTranslationsFile} from "@i18n/project/query";
import ProjectService from "@lib/Project/ProjectService";
import {DEFAULT_LANG} from "@i18n/language";
import PrivateHeader from "@components/Layout/Private/PrivateHeader";
import PrivateFooter from "@components/Layout/Private/PrivateFooter";
import "./PrivateLayout.scss";
import {ALWAYS_PUBLIC_PATHS, normalizePathname} from "@router/ContextualLayoutRoute";
import {usePublicTranslations} from "@i18n/i18nUtils";
import {useAuth} from "@msal/useAuth";
import {useUser} from "@lib/User/UserHooks";

export default function PrivateLayout() {

    const {data: user} = useUser();

    const {t} = usePublicTranslations();

    const [mobileMenuOpened, setMobileMenuOpened] = useState(false);
    const location = useLocation();

    const navigate = useNavigate();
    const derivedCountry = useAuthStore((s) => s.derivedCountry);
    const bootStatus = usePrivateBootStatus();
    const {run: runBootstrap} = usePrivateBootstrap();

    const projectId = useProjectId();
    const projectLanguage = useProjectLanguage();

    const {setOverlay, setOverlayMessage} = useAuthStore(
        useShallow(s => ({
            setOverlay: s.setOverlay,
            setOverlayMessage: s.setOverlayMessage,
        }))
    );

    const { logoutFull } = useAuth();

    const ranRef = useRef(false);

    useEffect(() => {

        if (ranRef.current) return;
        ranRef.current = true;

        (async () => {
            const token = await getValidAccessToken(false);
            const result = await runBootstrap({token, b2cCountry: derivedCountry});

            if (result.ok) {
                return;
            }

            const pathname = normalizePathname(location.pathname);
            const isAlwaysPublic = ALWAYS_PUBLIC_PATHS.has(pathname);

            if (isAlwaysPublic) {
                setOverlay(false);
                setOverlayMessage(null);
                return;
            }

            if (result.reason === "NOT_AUTHENTICATED") {
                navigate(buildRoute(AppRoute.WELCOME), {replace: true});
                return;
            }

            if (result.reason === "ME_USER_MISSING") {
                navigate(buildRoute(AppRoute.ON_BOARD), {replace: true});
                return;
            }

            if (result.reason === "COUNTRY_DISABLED" || result.reason === "COUNTRY_MISMATCH") {
                await logoutFull({
                    postLogoutRedirectToOpsReason: result.reason,
                });
                return;
            }

            setOverlay(false);
            setOverlayMessage(null);
            navigate(`${buildRoute(AppRoute.OPS)}?reason=${encodeURIComponent(result.reason)}`, {
                replace: true,
                state: {reason: result.reason},
            });

        })();

        return () => {
            ranRef.current = false
        };

    }, [derivedCountry, runBootstrap, location.pathname, navigate, setOverlay, setOverlayMessage]);

    useEffect(() => {
        if (bootStatus === "idle" || bootStatus === "loading") {
            setOverlay(true);
            setOverlayMessage(t("loading"));
        }
    }, [bootStatus, setOverlay, setOverlayMessage, t]);

    /**
     * Set language
     */
    useEffect(() => {
        if (bootStatus !== "ready") return;
        if (!projectLanguage) return;

        if (i18n.language !== projectLanguage) {
            i18n.changeLanguage(projectLanguage).then(r => console.log("i18n language changed", r));
        }
    }, [bootStatus, projectLanguage]);

    const translations = useProjectTranslationsFile({
        project: bootStatus === "ready" && projectId ? projectId : null,
        lang: (projectLanguage ?? DEFAULT_LANG) as any,
        getProjectTranslations: ProjectService.getTranslations,
        enabled: bootStatus === "ready" && !!projectId && !!projectLanguage,
    });

    const hasProjectBundle =
        !!projectLanguage && i18n.hasResourceBundle(projectLanguage, "project");

    useEffect(() => {
        if (bootStatus !== "ready") return;

        if (!projectId || !projectLanguage) {
            setOverlay(true);
            setOverlayMessage(t("loading"));
            return;
        }

        if (translations.isLoading || translations.isFetching) {
            setOverlay(true);
            setOverlayMessage(t("loading"));
            return;
        }

        if (translations.isError) {
            setOverlay(false);
            setOverlayMessage(null);
            navigate(`${buildRoute(AppRoute.OPS)}?reason=TRANSLATIONS_ERROR`, {
                replace: true, state: {reason: "TRANSLATIONS_ERROR"}
            });
            return;
        }

        setOverlay(false);
        setOverlayMessage(null);
    }, [
        bootStatus,
        projectId,
        projectLanguage,
        translations.isLoading,
        translations.isFetching,
        translations.isError,
        navigate,
        setOverlay,
        setOverlayMessage,
        t
    ]);

    useEffect(() => {
        setMobileMenuOpened(false);
    }, [location.pathname]);

    const translationsReady =
        !!projectId &&
        !!projectLanguage &&
        translations.status === "success" &&
        hasProjectBundle;

    if (bootStatus !== "ready" || !translationsReady) {
        return null;
    }

    return (
        <div className={`layout ${mobileMenuOpened ? "mobile-menu-opened" : "mobile-menu-closed"}`}>
            {mobileMenuOpened &&
                <div className="mobile-menu-overlay"></div>
            }

            <PrivateHeader mobileMenuOpened={mobileMenuOpened} setMobileMenuOpened={setMobileMenuOpened}/>

            <main className="layout__main">
                <Outlet/>
            </main>

            <PrivateFooter/>

            {user?.email === "ovidiu.crihana+fr02@jakala.com" &&
                <div>
                    <NavLink to={"/webview-test"}>Webview test</NavLink>
                    <span>-</span>
                    <NavLink to={"/i18n-test"}>i18n test</NavLink>
                    <span>-</span>
                    <NavLink to={"/form-test"}>Form test</NavLink>
                    <span>-</span>
                    <NavLink to={"/debug"}>Msal debug</NavLink>
                    <span>-</span>
                    <NavLink to={"/gtm-test"}>GTM debug</NavLink>
                </div>
            }
        </div>
    );
}